import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { Input } from '../CommonComponents/OPL/Input/Input';
import { StyledModalBody } from './AddClientModal.styles';
import { FormValues, validation } from './AddClientModal.meta';
import { Client, useAddClientMutation } from './api/useAddClientMutation';
import { queryKeys as clientSearchQKs } from '../ClientSearch/api/queryKeys';
import { addClient as adviserAreaAddClientAction } from '../AdviserArea/AdviserAreaActions';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';

interface Props {
  clientToClone?: Client;
  onClose(): void;
}

export const AddClientModal: React.FC<Props> = ({ clientToClone, onClose }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: clientToClone ? clientToClone.firstName : '',
      lastName: clientToClone ? clientToClone.lastName : '',
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: addClient,
    isLoading: isAddingClient,
    error: addClientError,
  } = useAddClientMutation();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const handleAddClient = async ({ firstName, lastName }: FormValues) => {
    try {
      const { clientId, newConsumerId } = await addClient({
        id: clientToClone?.id,
        firstName,
        lastName,
      });

      ADV.util.clearDashboardCache();

      dispatch(adviserAreaAddClientAction());

      queryClient.invalidateQueries(clientSearchQKs.clientList());

      ADV.util.Router.go2('ConsumerOverview', {
        clientId: clientToClone ? newConsumerId : clientId,
      });

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'add-client-modal-heading' }}>
      <ModalHeader id="add-client-modal-heading">
        {clientToClone
          ? `Clone ${clientToClone.firstName} ${clientToClone.lastName}`
          : 'Add a Client'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <form onSubmit={handleSubmit(handleAddClient)}>
        {addClientError && (
          <ModalBody>
            <Alert message={addClientError.message} variant="error" />
          </ModalBody>
        )}

        <StyledModalBody>
          <Input
            {...register('firstName')}
            type="text"
            label="First Name"
            formVariant={errors.firstName ? 'error' : undefined}
            helperText={errors.firstName?.message}
            maxLength={50}
          />

          <Input {...register('lastName')} type="text" label="Last Name" maxLength={50} />
        </StyledModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" isLoading={isAddingClient} ml="12">
            {clientToClone ? 'Clone' : 'Add'} Client
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
